import { defineStore } from 'pinia'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { computed, ref } from 'vue'

import { api } from '@/lib/api'
import { getPatientToken } from '@/lib/auth-token'
import { fetchItem } from '@/lib/lingodb'
import { isNative } from '@/lib/platform'
import type { RatingMode } from '@/lib/exercise-labels'
import {
  downloadItemMedia,
  isItemComplete,
  loadExerciseManifest,
  pruneExerciseManifest,
  pruneOrphanItems,
  saveExerciseManifest,
  type CachedItemMedia,
  type ExerciseManifest,
} from '@/lib/exercise-media-cache'
import { pruneTrainingHistory } from '@/lib/training-history-cache'
import { useDeleteQueueStore } from '@/stores/deleteQueue'
import { useResumableRunsStore } from '@/stores/resumableRuns'

/**
 * Patient-side exercise list + offline media-download pipeline.
 *
 * Drives the "Übung auswählen" picker at
 * resources/js/views/workspace/patient/ExercisesPick.vue. Lifecycle is the
 * same shape as resources/js/stores/topics.ts:
 *
 *   1. `preload()` runs once at app mount (from App.vue) and again after
 *      first-launch bootstrap (LaunchTerms.onSubmit, alongside topics).
 *      Idempotent — concurrent callers share one inflight promise.
 *   2. On invocation it hydrates `exercises` from the on-disk JSON index
 *      for instant UI, then refreshes from the server iff a patient token
 *      already exists. For every refreshed exercise it reconstitutes
 *      `downloadStatus` from the per-exercise manifest and starts a
 *      download pipeline for any exercise whose items aren't all cached.
 *   3. A 10-min "fresh" guard skips the network round-trip on close
 *      remounts (e.g. between launch-trial-patient and exercises-pick).
 *
 * Why per-device, not server-side: media completeness is a property of
 * THIS device. A fresh install on a second device should re-prepare even
 * though the exercise row already exists on the server. So the manifest
 * lives in Capacitor `Directory.Data`, not the database.
 */

const EXERCISES_INDEX_FILE = 'lingodb/exercises.json'
const FRESH_WINDOW_MS = 10 * 60 * 1000
/** How many item downloads to run in parallel within one exercise. */
const PER_EXERCISE_CONCURRENCY = 4

export interface Exercise {
  id: number
  patient_id: string
  name: string
  topic_id: number
  difficulties: number[]
  item_ids: number[]
  rating_mode: RatingMode
  created_at: string
  updated_at: string
  /** Therapist-controlled help allow-list (the 12 `VALID_HELPS` keys). Present only
   *  on therapist exercises (web embed today, tablet homework later); absent on
   *  patient-authored exercises, where `undefined` means "all helps available".
   *  Read at runtime by TrainingView via `lib/help-availability`. */
  available_helps?: string[]
  /** Both stats are server-optional today — the training screen will populate
   *  them when it lands. The picker treats undefined as 0 so we don't need
   *  a schema change to ship the UI. */
  runs_count?: number
  last_score_percent?: number
}

export type DownloadStatus = 'idle' | 'preparing' | 'ready' | 'error'

export const useExercisesStore = defineStore('exercises', () => {
  const exercises = ref<Exercise[]>([])
  const downloadStatus = ref<Record<number, DownloadStatus>>({})
  const status = ref<'idle' | 'hydrating' | 'loading' | 'ready' | 'error'>('idle')
  const lastFetchedAt = ref<Date | null>(null)
  const error = ref<string | null>(null)

  /** Shared promise so concurrent preload() callers fan in. */
  let inflight: Promise<void> | null = null
  /** Per-exercise download promise, so a second enqueueDownload() while one
   *  is already running for the same id just awaits it. */
  const downloadInflight = new Map<number, Promise<void>>()

  const isReady = computed(() => status.value === 'ready')

  function getById(id: number): Exercise | undefined {
    return exercises.value.find((e) => e.id === id)
  }

  function isFresh(): boolean {
    if (lastFetchedAt.value === null) return false
    return Date.now() - lastFetchedAt.value.getTime() < FRESH_WINDOW_MS
  }

  // --- Index file (instant cold-launch render) ---------------------------

  async function loadDiskIndex(): Promise<Exercise[]> {
    if (!isNative) return []
    try {
      const { data } = await Filesystem.readFile({
        path: EXERCISES_INDEX_FILE,
        directory: Directory.Data,
        encoding: 'utf8' as never,
      })
      const text = typeof data === 'string' ? data : await (data as Blob).text()
      const parsed = JSON.parse(text)
      return Array.isArray(parsed) ? (parsed as Exercise[]) : []
    } catch {
      return []
    }
  }

  async function saveDiskIndex(list: Exercise[]): Promise<void> {
    if (!isNative) return
    await Filesystem.writeFile({
      path: EXERCISES_INDEX_FILE,
      data: JSON.stringify(list),
      directory: Directory.Data,
      encoding: 'utf8' as never,
      recursive: true,
    })
  }

  // --- Download pipeline -------------------------------------------------

  /**
   * Compute the initial download status of an exercise from its manifest
   * alone (no network). Returns 'ready' iff every item_id has a complete
   * cache entry; otherwise 'preparing'. Used at hydrate-from-disk time so
   * the picker can render an accurate state before the first refresh.
   */
  async function statusFromManifest(ex: Exercise): Promise<DownloadStatus> {
    if (!isNative) return 'ready' // web has no offline cache to be incomplete
    const manifest = await loadExerciseManifest(ex.id)
    const allComplete = ex.item_ids.every((id) => isItemComplete(manifest[id]))
    return allComplete ? 'ready' : 'preparing'
  }

  /**
   * Download whatever is still missing for one exercise. Idempotent — if
   * the manifest already marks everything complete, this is a no-op. If a
   * download is in flight for this exercise, returns the existing promise.
   *
   * Concurrency model: PER_EXERCISE_CONCURRENCY items in flight at once
   * within one exercise. Exercises themselves are processed sequentially
   * by the caller (`refresh()`); the 15-min presigned-URL TTL is per-
   * request so finishing one exercise quickly beats parallelising
   * downloads of items across exercises that have their own TTL windows.
   *
   * Persists the manifest after every successful item so a kill mid-
   * download leaves real progress on disk.
   */
  async function enqueueDownload(ex: Exercise): Promise<void> {
    const existing = downloadInflight.get(ex.id)
    if (existing) return existing

    const run = (async () => {
      if (!isNative) {
        downloadStatus.value = { ...downloadStatus.value, [ex.id]: 'ready' }
        return
      }
      downloadStatus.value = { ...downloadStatus.value, [ex.id]: 'preparing' }

      const manifest = await loadExerciseManifest(ex.id)
      const todo = ex.item_ids.filter((id) => !isItemComplete(manifest[id]))
      if (todo.length === 0) {
        downloadStatus.value = { ...downloadStatus.value, [ex.id]: 'ready' }
        return
      }

      let failed = false
      let cursor = 0
      const next = (): number | undefined => (cursor < todo.length ? todo[cursor++] : undefined)

      async function worker(): Promise<void> {
        for (let id = next(); id !== undefined; id = next()) {
          try {
            // ETag-aware show endpoint. Items rarely change, so this is
            // usually a 200 first time then 304 on retries — both shapes
            // are handled by lib/lingodb.ts:fetchItem.
            const result = await fetchItem(id)
            const itemFull = result.body?.data
            // The cache lib needs the whole ItemFull: media (walks every
            // kind — image1/2/help, mouth, 6 audio variants — so the
            // training screen renders the help grid offline) PLUS the
            // lightweight item metadata (content, ipa) so the training
            // view doesn't have to round-trip per item at boot.
            if (itemFull === undefined) {
              // No payload (rare; usually a 304 with no body means we
              // should have hit a populated previous fetch). Skip and
              // let the next refresh retry.
              continue
            }

            let updated = await downloadItemMedia(itemFull, manifest[id])

            // Presigned media URLs have a 15-min TTL. If any download
            // failed it's most likely because we got handed stale URLs
            // (e.g. WKWebView URL-cache hit on the ETag'd item response).
            // Refetch once with a cache-buster and retry — completed
            // kinds are skipped by priorPath inside downloadItemMedia, so
            // this only re-attempts the failed ones against fresh URLs.
            if (!updated.complete) {
              const retry = await fetchItem(id, undefined, { bustCache: true })
              const fresh = retry.body?.data
              if (fresh !== undefined) {
                updated = await downloadItemMedia(fresh, updated)
              }
            }

            manifest[id] = updated
            // Persist after EVERY item so a mid-download kill keeps real
            // progress on disk. Cheap (small JSON, one item per exercise).
            await saveExerciseManifest(ex.id, manifest)

            if (!updated.complete) failed = true
          } catch (e) {
            console.warn(`exercise ${ex.id}: item ${id} fetch failed:`, e)
            failed = true
          }
        }
      }

      const workers = Array.from(
        { length: Math.min(PER_EXERCISE_CONCURRENCY, todo.length) },
        () => worker(),
      )
      await Promise.all(workers)

      // Final state: if every required item ended up complete, ready; otherwise
      // surface 'error' so the next refresh retries. We don't go back to
      // 'preparing' — that would imply work is still active.
      const allComplete = ex.item_ids.every((id) => isItemComplete(manifest[id]))
      downloadStatus.value = {
        ...downloadStatus.value,
        [ex.id]: allComplete ? 'ready' : failed ? 'error' : 'preparing',
      }
    })()
      .finally(() => {
        downloadInflight.delete(ex.id)
      })

    downloadInflight.set(ex.id, run)
    return run
  }

  // --- Public actions ----------------------------------------------------

  async function refresh(): Promise<void> {
    status.value = exercises.value.length > 0 ? 'hydrating' : 'loading'
    error.value = null
    try {
      const { data } = await api.get<{ data: Exercise[] }>('/patient/exercises')
      // Filter out anything the patient deleted while offline that hasn't
      // been DELETE'd server-side yet. Without this, an offline-delete
      // followed by an online-refresh would resurrect the row in the UI
      // until the delete queue flushes and the next refresh catches up.
      const deleteQueue = useDeleteQueueStore()
      await deleteQueue.refreshPending()
      const list = data.data.filter((ex) => !deleteQueue.pendingIds.has(ex.id))

      exercises.value = list
      lastFetchedAt.value = new Date()
      status.value = 'ready'

      await saveDiskIndex(list)

      // Seed downloadStatus from manifests so the UI is accurate before any
      // network pulls finish. Sweep orphaned per-item files for items no
      // longer referenced by any surviving exercise (e.g. after a delete).
      const seeded: Record<number, DownloadStatus> = { ...downloadStatus.value }
      for (const ex of list) {
        seeded[ex.id] = await statusFromManifest(ex)
      }
      downloadStatus.value = seeded
      await pruneOrphanItems(unionItemIds(list))

      // Kick off downloads for anything not already complete. Sequential
      // across exercises (see enqueueDownload's concurrency comment).
      for (const ex of list) {
        if (downloadStatus.value[ex.id] !== 'ready') {
          // Fire-and-await sequentially. A second call to enqueueDownload
          // for an exercise already in flight is a no-op via the inflight
          // map, so a fast follow-up refresh() doesn't double-pull.
          await enqueueDownload(ex)
        }
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      status.value = exercises.value.length > 0 ? 'ready' : 'error'
    }
  }

  async function preload(): Promise<void> {
    if (inflight) return inflight
    if (isFresh()) return

    inflight = (async () => {
      try {
        // 1. Hydrate from disk so the picker has something to show now.
        if (exercises.value.length === 0) {
          status.value = 'hydrating'
          const cached = await loadDiskIndex()
          if (cached.length > 0) {
            exercises.value = cached
            // Seed status from manifests so cards aren't all "preparing"
            // until the first refresh finishes.
            const seeded: Record<number, DownloadStatus> = {}
            for (const ex of cached) {
              seeded[ex.id] = await statusFromManifest(ex)
            }
            downloadStatus.value = seeded
          }
        }

        // 2. Refresh from the network only if we already have auth — first
        //    launch has no token yet, LaunchTerms re-fires preload() after
        //    bootstrap. Same gate topics.ts uses.
        if (isNative) {
          const token = await getPatientToken()
          if (!token) {
            status.value = exercises.value.length > 0 ? 'ready' : 'idle'
            return
          }
        }

        await refresh()
      } finally {
        inflight = null
      }
    })()

    return inflight
  }

  /**
   * Optimistically remove the exercise from local state and queue the
   * server-side DELETE for later. The picker (and any other view that
   * reads `exercises`) sees the row disappear immediately; the actual
   * DELETE goes out best-effort via {@link useDeleteQueueStore} which
   * retries on network reconnect and app resume.
   *
   * The disk index, the per-exercise media manifest, the cached
   * training-history list and any orphan item files are all swept here
   * — local-only cleanup, safe to do before the server has agreed. If
   * the DELETE eventually 404s server-side (already gone) the queue
   * drops it; that's the same outcome as a successful delete from the
   * patient's perspective.
   *
   * Never throws — offline is the expected case and the calling view
   * doesn't need to handle it specially anymore.
   */
  async function destroy(id: number): Promise<void> {
    // Persist the deletion intent BEFORE mutating local state so a kill
    // mid-call leaves a recoverable picture (queue says "delete X", local
    // exercise list still has X — next launch removes it from view and
    // re-enqueues the DELETE if it wasn't already).
    const deleteQueue = useDeleteQueueStore()
    await deleteQueue.enqueue(id)

    exercises.value = exercises.value.filter((e) => e.id !== id)
    const nextStatus = { ...downloadStatus.value }
    delete nextStatus[id]
    downloadStatus.value = nextStatus
    downloadInflight.delete(id)

    await pruneExerciseManifest(id)
    await pruneTrainingHistory(id)
    // Any mid-run snapshot for this exercise is moot once the exercise
    // itself is gone — drop it so a leftover entry doesn't keep the
    // ids set growing and doesn't confuse a future read.
    await useResumableRunsStore().drop(id)
    await saveDiskIndex(exercises.value)
    await pruneOrphanItems(unionItemIds(exercises.value))
  }

  return {
    // state
    exercises,
    downloadStatus,
    status,
    lastFetchedAt,
    error,
    // getters
    isReady,
    // actions
    preload,
    refresh,
    enqueueDownload,
    destroy,
    getById,
  }
})

function unionItemIds(list: Exercise[]): number[] {
  const set = new Set<number>()
  for (const ex of list) for (const id of ex.item_ids) set.add(id)
  return Array.from(set)
}

// Re-export so consumers (e.g. the picker view) can import the cached-media
// type for prop typing without reaching across the lib boundary.
export type { CachedItemMedia, ExerciseManifest }
